import { AccessTime, Add, Person } from "@mui/icons-material"
import { Box, Button, Grid2 as Grid, InputAdornment, LinearProgress, Skeleton, Stack, Typography } from "@mui/material"
import copy from "fast-copy"
import moment from 'moment'
import React from "react"
import { useLocation, useParams, useSearchParams } from "react-router-dom"
import { AddEditTask, CanonicalKeys, DateTimePicker, DeadlineAlert, EntityTimeline, GitHubLabel, MyPaper, HoursChart, InternatlServerError, ModuleMapperStack, NotFoundSection, TextField, UserAssignmentSection } from "../../components"
import { Association, AssociationObjectDetails, TagInfo } from "../../interfaces"
import { useAppDispatch } from "../../redux"
import { ITask, useLazyGetTaskByIdQuery, useUpdateTaskMutation } from "../../redux/services"
import { closeDialog, openDialog } from "../../redux/slices/dialog"
import { TAG_CANONICAL_NAMES, UUIDv4 } from "../../utils"
import { getObjectType } from "../bomReport/addEditBOMReport"
import { DataTableForm } from "../viewMaterial/dataSheet"
import { WorkLog } from "./workLog"

interface taskTagDetailInterface { label: string, tags: Partial<TagInfo>[], tag_canonical_name: CanonicalKeys, multiple: boolean }

const taskTagDetailInitialState = (other_canonical_names: CanonicalKeys[]): taskTagDetailInterface[] => {
    const [task_category, task_priority, task_type, task_label] = other_canonical_names;

    return [
        { label: "Task Category", tags: [], tag_canonical_name: task_category, multiple: false },
        { label: "Task Priority", tags: [], tag_canonical_name: task_priority, multiple: false },
        { label: "Task Type", tags: [], tag_canonical_name: task_type, multiple: false },
        { label: "Labels", tags: [], tag_canonical_name: task_label, multiple: true },
    ]
};

export const TaskDetail: React.FC<{
    users: string[],
    setCurrentPath?: (path: string) => void
}> = ({ users, setCurrentPath }) => {

    const dispatch = useAppDispatch();
    let params = useParams();
    const { id, object_type } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const location = useLocation();

    const parent_object_type = getObjectType(location);

    const workflow_canonical_name = React.useMemo(() => {
        switch(parent_object_type) {
            case "project":
                return TAG_CANONICAL_NAMES.project_task_workflow;
            case "product":
                return TAG_CANONICAL_NAMES.product_task_workflow;
            default:
                return TAG_CANONICAL_NAMES.proposal_task_workflow;
        }
    }, [parent_object_type]);

    const other_canonical_names = React.useMemo(() => {
        switch(parent_object_type) {
            case "project":
                return [
                    TAG_CANONICAL_NAMES.project_task_category,
                    TAG_CANONICAL_NAMES.project_task_priority,
                    TAG_CANONICAL_NAMES.project_task_type,
                    TAG_CANONICAL_NAMES.project_task_label,
                ];
            case "product":
                return [
                    TAG_CANONICAL_NAMES.product_task_category,
                    TAG_CANONICAL_NAMES.product_task_priority,
                    TAG_CANONICAL_NAMES.product_task_type,
                    TAG_CANONICAL_NAMES.product_task_label,
                ];
            default:
                return [
                    TAG_CANONICAL_NAMES.proposal_task_category,
                    TAG_CANONICAL_NAMES.proposal_task_priority,
                    TAG_CANONICAL_NAMES.proposal_task_type,
                    TAG_CANONICAL_NAMES.proposal_task_label,
                ];
        }
    }, [parent_object_type]);


    const [getTaskById, taskData] = useLazyGetTaskByIdQuery();
    const [updateTask, updateTaskData] = useUpdateTaskMutation();

    const [currentTask, setCurrentTask] = React.useState<ITask>();
    const [taskTagDetail, setTaskTagDetail] = React.useState<taskTagDetailInterface[]>(() => taskTagDetailInitialState([...other_canonical_names]));
    const [headingHeight, setHeadingHeight] = React.useState(60);
    const headingRef = React.useRef<HTMLDivElement | null>(null);

    const child_id = searchParams.get('child_id');
    const task_id = child_id ? child_id : params.task_id;

    const refetch = async () => {
        const _taskDetail = await getTaskById(`${task_id}`).unwrap();
        setCurrentTask(copy(_taskDetail));
        updateTaskTagDetail(_taskDetail);
        setCurrentPath?.(`${_taskDetail?.sequence_id ?? "..."}`)
    };

    const editTask = () => {
        dispatch(openDialog({
            title: "Update Task Detail",
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            body: <AddEditTask
                parentObjectId={`${id}`}
                parentObjectType={`${object_type}`}
                isEdit={true}
                data={currentTask}
                refetch={refetch} hideTags={true}
            />
        }));
    };

    const updateTaskTagDetail = (taskDetail: ITask) => {

        const _taskDetail = taskTagDetailInitialState([...other_canonical_names]);

        const [task_category, task_priority, task_type, task_label] = [...other_canonical_names];

        taskDetail?.tags_info?.forEach(_tag => {
            const tag = { ..._tag, _id: _tag.tag_id, value: _tag._id, label: _tag.tag_name };
            if (tag.tag_group_canonical_name === task_category) {
                _taskDetail[0].tags = [tag];
            };

            if (tag.tag_group_canonical_name === task_priority) {
                _taskDetail[1].tags = [tag];
            };

            if (tag.tag_group_canonical_name === task_type) {
                _taskDetail[2].tags = [tag];
            };

            if (tag.tag_group_canonical_name === task_label) {
                _taskDetail[3].tags.push(tag);
            };
        });

        setTaskTagDetail(_taskDetail);
    };

    const updateTaskViaAPI = (payload?: ITask) => {
        if (payload) {
            let hours_spent = payload.hours_spent;
            delete payload.hours_spent;
            updateTask({ id: `${task_id}`, payload: { ...payload, tags_info: payload.tags_info?.map(tag => ({ tag_id: tag.tag_id, tag_group_id: tag.tag_group_id })) ?? [] } });
            payload.hours_spent = hours_spent;
        }
    };

    const updateTaskTags = (tags: Partial<TagInfo>[], index: number) => {
        taskTagDetail[index].tags = tags;
        setTaskTagDetail(taskTagDetail);

        let newState = currentTask;

        if (newState) {
            const tags_info: Partial<TagInfo>[] = [];

            taskTagDetail.forEach(task => {
                task.tags.forEach(tag => {
                    tags_info.push({
                        tag_group_id: tag.tag_group_id,
                        tag_id: tag.tag_id || tag._id
                    })
                })
            })

            updateTaskViaAPI({ ...newState, tags_info });
        }
    };

    const updateTaskState = (key: keyof ITask, value: any, hitAPI: boolean = false) => {
        let newState = currentTask;

        if (newState) {
            newState[key] = value as never;
            setCurrentTask({ ...newState });
        };

        if (hitAPI) {
            updateTaskViaAPI(newState)
        };
    };

    const openLogTime = () => {
        dispatch(openDialog({
            title: "Log Time (in hours)",
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            maxWidth: 'xl',
            body: <WorkLog
                task_id={`${task_id}`}
                task_sequence_id={`${currentTask?.sequence_id ?? ""}`}
                task_name={`${currentTask?.name ?? ""}`}
                onSuccess={() => {
                    dispatch(closeDialog());
                    refetch();
                }} />
        }));
    };

    const linkItemToTask = (object_type: Association['object_type'], links: AssociationObjectDetails[], unlinks: AssociationObjectDetails[]) => {

        let newState = currentTask;

        if (newState) {
            if (newState.associations) {
                let isUpdated: boolean = false;
                newState.associations = newState.associations.map(assocation => {
                    if (assocation.object_type === object_type) {
                        isUpdated = true;
                        assocation.object_details = links
                    }
                    return assocation;
                });

                if (!isUpdated) {
                    newState.associations.push({ object_details: links, object_type });
                }
            } else {
                newState.associations = [{ object_details: links, object_type }];
            }
            setCurrentTask({ ...newState });
        }
    };

    const isTaskFetching = taskData.isFetching || taskData.isLoading;

    React.useEffect(() => {
        refetch();
        // eslint-disable-next-line
    }, [task_id, child_id]);

    React.useEffect(() => {
        if (headingRef.current) {
            if (headingRef.current.clientHeight !== headingHeight) {
                setHeadingHeight(headingRef.current.clientHeight);
            }
        }
    }, [headingRef, isTaskFetching, task_id, taskData, headingHeight]);

    if (taskData.isError) {
        return <InternatlServerError message={(taskData?.error as any)?.data?.title ?? "Something went wrong, Unable to fetch task"} onRetry={refetch} />
    }

    if (!isTaskFetching && taskData.isSuccess && !taskData.data) {
        return <NotFoundSection message="Task detail not found" />
    }

    return <Box sx={{ height: "100%", pb: 1, position: "relative" }}>
        {/* Header */}
        <Stack ref={headingRef} sx={{
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1051,
            position: "absolute",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
        }}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{
                width: "100%",
                px: 2,
                py: 1,
            }}>
                <Stack direction={"column"} sx={{ alignItems: "flex-start", width: { xs: "100%", sm: "90%" } }}>

                    {!isTaskFetching && <>
                        <Typography variant="body1" sx={{ fontFamily: "htrts_medium", wordBreak: "break-word" }}>
                            {`${currentTask?.name ?? ''}`}
                            {updateTaskData.isLoading && <Typography variant="caption" color="textSecondary" component={"span"}>&nbsp;&nbsp;&nbsp;Saving...</Typography>}
                        </Typography>
                        <Typography color="textSecondary" variant="body2" sx={{
                            fontFamily: "htrts_regular",
                            wordBreak: "break-word"
                        }}>{`${currentTask?.description ?? ''}`}</Typography>
                    </>}

                    {isTaskFetching && <Box sx={{ width: "300px" }}>
                        <Skeleton width={"60%"} />
                        <Skeleton width={"100%"} />
                    </Box>}

                </Stack>

                <Button variant="contained" size="small" disabled={isTaskFetching} color="primary" onClick={editTask}>
                    Edit Task
                </Button>
            </Stack>

            {/* Saving Loader */}
            {updateTaskData.isLoading && <Box sx={{ width: "100%" }}>
                <LinearProgress />
            </Box>}
        </Stack>

        {/* Task Detail */}
        <Stack direction={"row"} sx={{ height: "100%", pt: `${headingHeight + 4}px`, pb: 0 }}>
            {/* Comment */}
            <Stack sx={{ height: "100%", width: { xs: '100%', sm: '75%' }, overflowY: "auto", overflowX: "clip", padding: 1, paddingTop: 0, }}>
                {/* Checklist Forms */}
                <Stack gap={2} p={1}>
                    {currentTask?.associations?.filter(asso => asso.object_type === 'dataTableForm')?.[0]?.object_details?.map(object => {
                        return <Box key={object._id}>
                            <Typography variant="body2" sx={{ fontFamily: "htrts_medium", fontSize: 13 }} gutterBottom>{object.sequence_id}:</Typography>
                            <DataTableForm
                                type="checklist"
                                sequenceId={`${currentTask?.sequence_id ?? '...'}`}
                                associations={currentTask?.associations ?? []}
                                sheet_id={object._id}
                                preview={false}
                                hideHeader={true}
                                refetch={refetch}
                                hideAutoPopulate={true}
                                linkDataTableTemplate={(links: any[], unlinks: any[]) => Promise.resolve()}
                            />
                        </Box>
                    })}
                </Stack>

                {isTaskFetching && <>
                    {[1].map(task => <Stack key={task} direction={"row"} gap={2} sx={{ marginTop: 2, paddingX: 2, paddingRight: 4 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Skeleton variant="rounded" width={"100%"} height={'124px'} />
                    </Stack>)}
                </>}

                {/* Comment Section */}
                {!isTaskFetching && currentTask && task_id && <Box sx={{ pb: 2, mt: 1 }}>
                    <EntityTimeline objectId={`${task_id}`} objectType={'task'} />
                </Box>}
            </Stack>

            {/* Task Detail */}
            <Stack sx={{ height: "100%", width: { xs: '100%', sm: '25%' }, minWidth: '300px', overflow: "scroll", padding: 1 }}>
                {/* Tags Details */}
                <Stack gap={2}>
                    <MyPaper>
                        <Stack gap={2}>
                            {taskTagDetail.map((taskTagDetail, index) => {
                                return <GitHubLabel
                                    key={index}
                                    title={taskTagDetail.label}
                                    loading={isTaskFetching}
                                    multiple={taskTagDetail.multiple}
                                    tags={copy(taskTagDetail.tags)}
                                    tagCanonicalName={taskTagDetail.tag_canonical_name}
                                    onUpdate={(updatedTags) => updateTaskTags(updatedTags, index)}
                                />
                            })}
                        </Stack>
                    </MyPaper>

                    {/* User Assignment */}
                    <UserAssignmentSection workflow_canonical_name={workflow_canonical_name} objectId={`${task_id}`} objectType="task" users={users} onUpdated={() => {
                        setSearchParams({ child_id: child_id ? `${child_id}` : `${task_id}`, reloadTimeline: UUIDv4() });
                    }} />

                    {/* Task Management */}
                    <MyPaper>
                        <Stack gap={2}>
                            <Typography gutterBottom variant="body2" sx={{ fontSize: 13, fontFamily: "htrts_medium" }}>Task Management</Typography>

                            {isTaskFetching && <>
                                <Skeleton variant="rounded" width={"100%"} height={"56px"} />
                                <Skeleton variant="rounded" width={"100%"} height={"56px"} />
                                <Skeleton variant="rounded" width={"100%"} height={"56px"} />
                            </>}

                            {!isTaskFetching && <Grid container spacing={1} rowSpacing={2}>
                                {/* Deadline */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <DateTimePicker
                                        size="small"
                                        value={currentTask?.deadline ? moment(currentTask?.deadline) : null}
                                        onChange={(current_date) => updateTaskState('deadline', current_date, true)}
                                        label="Deadline"
                                        slotProps={{
                                            textField: {
                                                InputLabelProps: {
                                                    shrink: true,
                                                }
                                            }
                                        }}
                                        sx={{ width: "100%" }}
                                    />
                                </Grid>

                                {/* Budgeted Hours */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        variant="outlined"
                                        label={`Budgeted Hours`}
                                        fullWidth
                                        onChange={(e) => {
                                            updateTaskState('hours_estimated', +e.target.value)
                                        }}
                                        type="number"
                                        inputMode="numeric"
                                        slotProps={{
                                            input: {
                                                type: 'number',
                                                endAdornment: <InputAdornment position="start">hrs</InputAdornment>,
                                            },
                                            inputLabel: { shrink: true }
                                        }}
                                        size="small"
                                        value={(!(currentTask?.hours_estimated) || currentTask?.hours_estimated === 0) ? '' : +currentTask?.hours_estimated}
                                        onBlur={() => updateTaskViaAPI(currentTask)}
                                    />
                                </Grid>

                                {/* Log Time */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Button
                                        sx={{ width: "fit-content" }}
                                        variant="outlined"
                                        size="small"
                                        endIcon={<Add />}
                                        onClick={(e) => openLogTime()}
                                    >
                                        Log Time
                                    </Button>
                                </Grid>

                                {/* Actual Hours */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        variant="outlined"
                                        label={`Actual Hours`}
                                        placeholder={"No of hours"}
                                        type="number"
                                        inputMode="numeric"
                                        slotProps={{
                                            input: {
                                                type: 'number'
                                            }
                                        }}
                                        value={`${currentTask?.hours_spent ?? "0"} hrs`}
                                        viewOnly={true}
                                    />
                                </Grid>

                                {/* Status */}
                                {currentTask?.deadline && <Grid size={{ xs: 12 }}>
                                    <DeadlineAlert deadline={currentTask?.deadline} />
                                </Grid>}
                            </Grid>}

                            {/* Hours Chart */}
                            <HoursChart
                                hoursEstimated={currentTask?.hours_estimated ?? 0}
                                hoursSpent={currentTask?.hours_spent ?? 0}
                            />
                        </Stack>
                    </MyPaper>

                    {/* Linking Section */}
                    <MyPaper>
                        <Stack gap={2}>
                            <Typography gutterBottom variant="body2" sx={{ fontSize: 13, fontFamily: "htrts_medium" }}>Link</Typography>

                            {/* Link/Unlink Checklist to Task */}
                            <ModuleMapperStack
                                variant="Checklist Form"
                                title="Checklist Forms"
                                buttonName=""
                                objectId={`${task_id}`}
                                objectType={'task'}
                                parentObjectId={`${id}`}
                                parentObjectType={`${object_type}` as Association['object_type']}
                                linkUnlinkObjectType={'dataTableForm'}
                                stacks={currentTask?.associations?.filter(asso => asso.object_type === 'dataTableForm')?.[0]?.object_details ?? []}
                                onUpdate={(itemsAdded, itemsRemoved) => linkItemToTask('dataTableForm', itemsAdded, itemsRemoved)}
                                loading={isTaskFetching}
                                enableNavigation={false}
                            />

                            {/* Link/Unlink BOM to Task */}
                            <ModuleMapperStack
                                variant="bom"
                                title="BOM Link"
                                buttonName=""
                                objectId={`${task_id}`}
                                objectType={'task'}
                                parentObjectId={`${id}`}
                                parentObjectType={`${object_type}` as Association['object_type']}
                                linkUnlinkObjectType={'bom'}
                                stacks={currentTask?.associations?.filter(asso => asso.object_type === 'bom')?.[0]?.object_details ?? []}
                                onUpdate={(itemsAdded, itemsRemoved) => linkItemToTask('bom', itemsAdded, itemsRemoved)}
                                loading={isTaskFetching}
                                enableNavigation={true}
                            />

                            {/* Link/Unlink Material to Task */}
                            <ModuleMapperStack
                                variant="material"
                                title="Material Link"
                                buttonName=""
                                objectId={`${task_id}`}
                                objectType={'task'}
                                parentObjectId={`${id}`}
                                parentObjectType={`${object_type}` as Association['object_type']}
                                linkUnlinkObjectType={'material'}
                                stacks={currentTask?.associations?.filter(asso => asso.object_type === 'material')?.[0]?.object_details ?? []}
                                onUpdate={(itemsAdded, itemsRemoved) => linkItemToTask('material', itemsAdded, itemsRemoved)}
                                loading={isTaskFetching}
                                enableNavigation={true}
                            />

                            {/* Link/Unlink Document to Task */}
                            <ModuleMapperStack
                                variant="document"
                                title="Document Link"
                                buttonName=""
                                objectId={`${task_id}`}
                                objectType={'task'}
                                parentObjectId={`${id}`}
                                parentObjectType={`${object_type}` as Association['object_type']}
                                linkUnlinkObjectType={'document'}
                                stacks={currentTask?.associations?.filter(asso => asso.object_type === 'document')?.[0]?.object_details ?? []}
                                onUpdate={(itemsAdded, itemsRemoved) => linkItemToTask('document', itemsAdded, itemsRemoved)}
                                loading={isTaskFetching}
                                enableNavigation={true}
                            />
                        </Stack>
                    </MyPaper>

                    {/* General Information */}
                    <MyPaper>
                        <Stack gap={2} sx={{ pb: 2 }}>
                            <Typography gutterBottom variant="body2" sx={{ fontSize: 13, fontFamily: "htrts_medium" }}>General Information</Typography>


                            {isTaskFetching && <>
                                <Skeleton width={"100%"} height={'20px'} />
                                <Skeleton width={"100%"} height={'20px'} />
                                <Skeleton width={"100%"} height={'20px'} />
                                <Skeleton width={"100%"} height={'20px'} />
                            </>}

                            {!isTaskFetching && [
                                {
                                    icon: <AccessTime fontSize="small" color="secondary" />,
                                    label: `Created At : ${moment(currentTask?.created_time).format('lll')}`
                                },
                                {
                                    icon: <Person fontSize="small" color="secondary" />,
                                    label: `Created By : ${currentTask?.created_user}`
                                },
                                {
                                    icon: <AccessTime fontSize="small" color="secondary" />,
                                    label: `Modified At : ${currentTask?.modified_time ? moment(currentTask?.modified_time).format('lll') : '-'}`
                                },
                                {
                                    icon: <Person fontSize="small" color="secondary" />,
                                    label: `Modified By : ${currentTask?.modified_user ?? "-"}`
                                },
                            ].map(_ => <Stack gap={1} direction={"row"} alignItems={"center"}>
                                {_.icon}
                                <Typography sx={{fontSize: 13}} variant="body2" color="textSecondary" >{_.label}</Typography>
                            </Stack>)}
                        </Stack>
                    </MyPaper>
                </Stack>
            </Stack>
        </Stack>
    </Box>
}